import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, BookOpen, ChevronRight, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/Logo";
import MenuDropzone from "@/components/menu/MenuDropzone";
import AnalyzingSteps from "@/components/menu/AnalyzingSteps";
import { runAnalysis } from "@/pages/MenuAnalyzer";
import api, { errMsg } from "@/lib/api";
import { getPending, clearPending } from "@/lib/menuFunnel";
import { dateFr } from "@/lib/format";
import { t } from "@/i18n";

export default function MyMenu() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const claimed = useRef(false);

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    const p = getPending();
    if (params.get("claim") && p) {
      setBusy("claim");
      api.post(`/menu-analysis/${p.id}/claim`, { token: p.token })
        .then(() => { clearPending(); nav(`/ma-carte/${p.id}`, { replace: true }); })
        .catch((e) => { clearPending(); setError(errMsg(e)); setBusy(null); });
    }
    api.get("/menu-analyses").then(({ data }) => setList(data)).catch(() => setList([]));
  }, [nav, params]);

  const onFile = async (f) => {
    setBusy(f.name);
    setError("");
    try {
      const data = await runAnalysis(f);
      await api.post(`/menu-analysis/${data.id}/claim`, { token: data.token });
      nav(`/ma-carte/${data.id}`);
    } catch (e) {
      setError(errMsg(e));
      setBusy(null);
    }
  };

  if (busy === "claim") return <div className="flex h-64 items-center justify-center" data-testid="menu-claiming"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <>
      <PageHeader eyebrow={t("myMenu.analyse_de_carte")} title={t("common.ma_carte")} description={t("myMenu.importez_votre_carte_l_ia_detecte_vos_plat")} />
      {busy ? <AnalyzingSteps fileName={busy} /> : (
        <div className="space-y-6">
          {error && <p className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-600 dark:text-rose-400" data-testid="menu-error"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
          <MenuDropzone onFile={onFile} />
          {list?.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold">{t("myMenu.analyses_precedentes")}</h2>
              <div className="space-y-2" data-testid="menu-analyses-list">
                {list.map((a) => (
                  <Link key={a.id} to={`/ma-carte/${a.id}`} className="flex items-center gap-3 rounded-xl border bg-card p-3.5 transition-colors hover:border-emerald-500/40" data-testid={`menu-analysis-item-${a.id}`}>
                    <BookOpen className="h-4 w-4 shrink-0 text-emerald-500" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{a.restaurant_name || t("myMenu.carte_analysee")}</p>
                      <p className="text-xs text-muted-foreground">{dateFr(a.created_at)} · {t("menu.n_dishes", { v0: a.dish_count })}{a.imported_count ? t("myMenu.x_ajoutes_aux_recettes", { v0: a.imported_count }) : ""}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
